// A deck and everything it owns, as one movable unit (TODO §12.4.5 / §12.6).
//
// Duplicate, import and export are the same three steps in different orders — read the
// deck plus its grid bodies and image blobs, rewrite every id, write it back — so the id
// remap lives here once rather than being re-derived per feature.
//
// **The rule the remap exists to enforce:** a copied deck may share NO id with its source.
// Not the deck id, not slide ids, not `griddDocId`s, not `diagramDocId`s, not `assetId`s.
// Two decks pointing at one `griddDocId` are not two decks — they are one grid body aliased
// twice, and editing the copy silently rewrites the original (§12.4.5). The same applies to
// an imported file, which may well be a re-import of a deck already sitting in this database
// (§12.6.2).
//
// **§17.1.5 — THE ONE EXCEPTION, and it is deliberate.** A slide carrying a `link` is a
// knowledge nugget's slide (§17), and it keeps the nugget's `griddDocId`s, `diagramDocId`s
// and `assetId`s **verbatim**. That aliasing is not the bug the paragraph above describes —
// it IS the feature: the body exists once, under the nugget's owner key, and every deck that
// links the nugget points at it. A duplicated deck is therefore a SECOND USER of the nugget
// rather than a private copy of it (§17.5.1). Do not "fix" this back into a full remap; the
// copy would then edit a body nothing else points at and the link would be a lie.
//
// §21/§12.3 A diagram body is the same shape of thing and gets the same treatment: a fresh
// `diagramDocId` per copy, and the images INSIDE a diagram ride along for free — they are
// ordinary `assets` rows (§4.4.2), so the asset pass below already carries them. That is
// what §4.4.2 bought: no new concept here, at all.
//
// §26/§9 A gantt body is the same again, and simpler than either: it is one JSON record with
// no blobs inside it, so it needs a fresh `ganttDocId` per copy and nothing else.
//
// §15/§9 A chart body is the gantt case exactly: one JSON record, no blobs, a fresh
// `chartDocId` per copy. Its stylesheet half needs nothing here at all — chart styles live in
// `deck.styleSheet`, which rides through in the `...bundle.deck` spread like every other deck
// field, and holds colour NAMES rather than ids (§6.2.2), so there is nothing in it to remap.

import {
  remapSlideContents,
  type ContentRefKind,
  type RemapRef,
} from "@/lib/contentRefs";
import {
  createDeck,
  getDeck,
  getNugget,
  getOwnedAssets,
  loadNuggetBodies,
  loadPersistedState,
  putAsset,
  putDiagramDoc,
  putChartDoc,
  putGanttDoc,
  putGriddDoc,
  putNuggetRecord,
} from "@/lib/idb";
import { deckOwner, nuggetOwner } from "@/lib/ownerKey";
import type { NuggetRecord } from "@/types/nugget";
import type { ChartDocument } from "@/types/chart";
import type { DiagramDocument } from "@/types/diagram";
import type { GanttDocument } from "@/types/gantt";
import type { DeckDocument, GriddDocument } from "@/types/document";

export interface BundleAsset {
  assetId: string;
  mime: string;
  blob: Blob;
}

// §17.5.3 One nugget as it travels inside a `.deckai`: the record plus everything it owns,
// so the deck opens WHOLE on a machine that has never seen this library. Nested rather than
// merged into the deck's own maps because the two have different owners on the way back in —
// a deck's bodies are written under the deck, a nugget's under `nugget:<id>`.
export interface NuggetBundle {
  record: NuggetRecord;
  grids: Record<string, GriddDocument>;
  diagrams: Record<string, DiagramDocument>;
  gantts: Record<string, GanttDocument>;
  charts: Record<string, ChartDocument>;
  assets: BundleAsset[];
}

export interface DeckBundle {
  deck: DeckDocument;
  // Grid bodies keyed by griddDocId, as they sit in griddSlice / the `griddDocs` store.
  grids: Record<string, GriddDocument>;
  // §21/§12.3 Diagram bodies keyed by diagramDocId. Optional on read so a bundle parsed from
  // an older `.deckai` file (FORMAT_VERSION 1, written before diagrams existed) is a valid
  // bundle rather than a parse error.
  diagrams: Record<string, DiagramDocument>;
  // §26/§9 Gantt bodies keyed by ganttDocId. Optional on read for the same reason diagrams
  // are: a `.deckai` written at FORMAT_VERSION 1 or 2 has none, and that is a valid bundle.
  gantts: Record<string, GanttDocument>;
  // §15/§9 Chart bodies keyed by chartDocId. Optional on read for the reason `gantts` is: a
  // `.deckai` written at FORMAT_VERSION 1–3 has none, and that is a valid bundle.
  charts: Record<string, ChartDocument>;
  assets: BundleAsset[];
  // §17.5.3 The nuggets this deck's linked slides need. Optional on read so a bundle parsed
  // from an older `.deckai` (FORMAT_VERSION 1–3) is a valid bundle rather than a parse error,
  // exactly as `diagrams` and `gantts` are.
  nuggets: NuggetBundle[];
}

// Read a whole deck out of IndexedDB. Grid bodies come from the `deckId` index rather than
// by walking the slides, so a body whose slide was archived still travels with the deck.
export async function readDeckBundle(
  deckId: string,
): Promise<DeckBundle | null> {
  const deck = await getDeck(deckId);
  if (!deck) return null;
  // `loadPersistedState` already shapes both body maps exactly like `DeckBundle`'s. It reads
  // diagrams by OWNER key, so a deck-owned body travels and a nugget-owned one (§17) does
  // not — which is right: a shared body belongs to the nugget, not to this copy.
  const {
    griddDocs: grids,
    diagramDocs: diagrams,
    ganttDocs: gantts,
    chartDocs: charts,
  } = await loadPersistedState(deckId);
  const assets = (await getOwnedAssets(deckOwner(deckId))).map((a) => ({
    assetId: a.assetId,
    mime: a.mime,
    blob: a.blob,
  }));
  // §17.5.3 …and then the nuggets this deck's linked slides need, each with everything it
  // owns. Note the asymmetry with the three maps above, and that it is the point:
  // `loadPersistedState` reads BOTH the deck's own bodies and the linked nuggets' (so the
  // editor renders), whereas a bundle must keep them apart (so the writer can file each half
  // under the right owner). `grids` above therefore holds nugget bodies too — harmless on the
  // way out, because the remap's survivor filter drops anything no REMAPPED slide references,
  // and a linked slide's ids are never registered for remapping.
  const nuggets: NuggetBundle[] = [];
  for (const nuggetId of new Set(
    deck.slides.flatMap((slide) => (slide.link ? [slide.link.nuggetId] : [])),
  )) {
    const record = await getNugget(nuggetId);
    // A link whose nugget is gone: the slide still travels, materialized and complete, and
    // the import drops the dead link on the receiving side's first open (§17.2.5).
    if (!record) continue;
    const bodies = await loadNuggetBodies(nuggetId);
    nuggets.push({
      record,
      grids: bodies.griddDocs,
      diagrams: bodies.diagramDocs,
      gantts: bodies.ganttDocs,
      charts: bodies.chartDocs,
      assets: bodies.assets.map((a) => ({
        assetId: a.assetId,
        mime: a.mime,
        blob: a.blob,
      })),
    });
  }
  // …and then take the nuggets' bodies back OUT of the deck's three maps.
  //
  // `loadPersistedState` deliberately returns both halves merged — it exists to hydrate an
  // editor, which has to render a linked slide — but a bundle keeps them apart, because the
  // writer files each half under a different owner. Left merged they would be written into
  // the archive twice, and on import the deck-level copies would be silently dropped by the
  // survivor filter in `remapBundle` anyway: dead weight in the zip and a puzzle for whoever
  // unzips one by hand.
  for (const nugget of nuggets) {
    for (const docId of Object.keys(nugget.grids)) delete grids[docId];
    for (const docId of Object.keys(nugget.diagrams)) delete diagrams[docId];
    for (const docId of Object.keys(nugget.gantts)) delete gantts[docId];
    for (const docId of Object.keys(nugget.charts)) delete charts[docId];
  }
  return { deck, grids, diagrams, gantts, charts, assets, nuggets };
}

// Write a bundle as a NEW deck. Assumes the ids are already fresh (call `remapBundle`
// first) — this function does not check, because import and duplicate both remap and a
// second pass would only mask a caller that forgot.
export async function writeDeckBundle(bundle: DeckBundle): Promise<string> {
  const deckId = bundle.deck.id;
  // §17.5.3 The nuggets first of all, because the deck's linked slides point straight at
  // their bodies — and **adopt or create by `nuggetId`**: if this database already has that
  // nugget, the local copy is kept WHATEVER its rev, and only the link is made. A `.deckai`
  // is a snapshot someone mailed you, not a sync channel; server-wins (§17.10) is a rule
  // about a server, and importing a six-month-old archive must not roll a live nugget back.
  //
  // The return value says which ids were adopted, so the caller can say so in the toast.
  const adopted: string[] = [];
  for (const nugget of bundle.nuggets ?? []) {
    const { nuggetId } = nugget.record;
    if (await getNugget(nuggetId)) {
      adopted.push(nuggetId);
      continue;
    }
    const owner = nuggetOwner(nuggetId);
    for (const [docId, doc] of Object.entries(nugget.grids)) {
      await putGriddDoc(owner, docId, doc);
    }
    for (const [docId, doc] of Object.entries(nugget.diagrams)) {
      await putDiagramDoc(owner, docId, doc);
    }
    for (const [docId, doc] of Object.entries(nugget.gantts)) {
      await putGanttDoc(owner, docId, doc);
    }
    for (const [docId, doc] of Object.entries(nugget.charts ?? {})) {
      await putChartDoc(owner, docId, doc);
    }
    for (const asset of nugget.assets) {
      await putAsset(asset.assetId, owner, asset.blob, asset.mime);
    }
    await putNuggetRecord(nugget.record);
  }
  lastAdoptedNuggets = adopted;
  // Children first: a deck record is what the URL resolves against, so it must not become
  // reachable until the grids and blobs it points at are actually there.
  for (const [docId, doc] of Object.entries(bundle.grids)) {
    await putGriddDoc(deckOwner(deckId), docId, doc);
  }
  // §4.3.3 Written under this deck's OWNER key, never a raw id — the one rule diagrams have
  // that grids do not, and the reason they need no migration when §17 lands.
  for (const [docId, doc] of Object.entries(bundle.diagrams ?? {})) {
    await putDiagramDoc(deckOwner(deckId), docId, doc);
  }
  // §26/§9 Same owner-key rule as the diagrams above.
  for (const [docId, doc] of Object.entries(bundle.gantts ?? {})) {
    await putGanttDoc(deckOwner(deckId), docId, doc);
  }
  // §15/§9 …and the charts, on exactly the same terms.
  for (const [docId, doc] of Object.entries(bundle.charts ?? {})) {
    await putChartDoc(deckOwner(deckId), docId, doc);
  }
  for (const asset of bundle.assets) {
    await putAsset(asset.assetId, deckOwner(deckId), asset.blob, asset.mime);
  }
  await createDeck(bundle.deck);
  return deckId;
}

// §17.5.3 Which nuggets the last `writeDeckBundle` adopted rather than created. A
// module-level latch rather than a second return value, because `writeDeckBundle` returns a
// deck id to four call sites and only one of them — the import toast — has anything to say
// about nuggets. Read it immediately after the call or not at all.
let lastAdoptedNuggets: string[] = [];

export function takeAdoptedNuggets(): string[] {
  const ids = lastAdoptedNuggets;
  lastAdoptedNuggets = [];
  return ids;
}

// Rewrite every id in the bundle. Pure apart from `crypto.randomUUID()`; safe to call on a
// bundle that came from a file, from another deck, or from this same deck.
export function remapBundle(
  bundle: DeckBundle,
  opts: { title?: string } = {},
): DeckBundle {
  const gridIds = new Map<string, string>();
  const diagramIds = new Map<string, string>();
  const ganttIds = new Map<string, string>();
  const chartIds = new Map<string, string>();
  const assetIds = new Map<string, string>();

  const remapId = (map: Map<string, string>, old: string): string => {
    let next = map.get(old);
    if (next === undefined) {
      next = crypto.randomUUID();
      map.set(old, next);
    }
    return next;
  };

  // §17.4.3 The per-kind maps, behind the one shared walker (`contentRefs.ts`). The remap
  // used to be written out kind by kind HERE; Break link needs the same walk for one slide,
  // and a second copy is a second place to forget the kind that gets added next.
  const mapFor: Record<ContentRefKind, Map<string, string>> = {
    grid: gridIds,
    diagram: diagramIds,
    gantt: ganttIds,
    chart: chartIds,
    asset: assetIds,
  };
  const remapRef: RemapRef = (kind, id) => remapId(mapFor[kind], id);

  const now = new Date().toISOString();
  // §24.1.1 The deck's logo is an asset reference that is NOT in any slide's contents, and
  // this function used to collect new ids from the slides alone — so a copy kept the source
  // deck's `assetId`, the survivor filter below never saw that id, and the blob was left
  // behind. The copy then renders the ORIGINAL deck's logo until the original is deleted,
  // and silently stops afterwards. Remapping it here fixes both halves at once: the id
  // becomes the copy's own, and registering it in `assetIds` is exactly what makes the
  // filter carry the blob across. Duplicate reproduces the old behaviour in one click.
  const logo = bundle.deck.logo
    ? {
        ...bundle.deck.logo,
        assetId: remapId(assetIds, bundle.deck.logo.assetId),
      }
    : undefined;

  const deck: DeckDocument = {
    ...bundle.deck,
    id: crypto.randomUUID(),
    title: opts.title ?? bundle.deck.title,
    ...(logo ? { logo } : {}),
    // §17.5.1 A LINKED slide keeps its link and the nugget's ids verbatim — see the
    // exception paragraph at the top of this file. Only its slide `id` is minted fresh,
    // because slide ids are deck-local whatever the contents are. Not registering those
    // grid/asset ids for remapping is also what keeps the survivor filter below from
    // copying the nugget's bodies into this deck: they belong to the nugget, they are
    // carried by `bundle.nuggets`, and a private copy would silently un-share the slide.
    slides: bundle.deck.slides.map((slide) =>
      slide.link
        ? { ...slide, id: crypto.randomUUID() }
        : {
            ...slide,
            id: crypto.randomUUID(),
            contents: remapSlideContents(slide.contents, remapRef),
          },
    ),
    // A copy is a new document, not a new revision of the old one: it starts its own
    // history at rev 0 and dates from now.
    meta: { ...bundle.deck.meta, createdAt: now, updatedAt: now, rev: 0 },
  };

  // Only bodies/blobs the remapped slides actually reference survive. A grid body with no
  // slide pointing at it is already invisible in the source deck; copying it forward would
  // just seed the copy with orphans.
  const grids: Record<string, GriddDocument> = {};
  for (const [oldId, newId] of gridIds) {
    const doc = bundle.grids[oldId];
    if (doc) grids[newId] = doc;
  }
  const diagrams: Record<string, DiagramDocument> = {};
  for (const [oldId, newId] of diagramIds) {
    const doc = (bundle.diagrams ?? {})[oldId];
    if (doc) diagrams[newId] = doc;
  }
  const gantts: Record<string, GanttDocument> = {};
  for (const [oldId, newId] of ganttIds) {
    const doc = (bundle.gantts ?? {})[oldId];
    if (doc) gantts[newId] = doc;
  }
  const charts: Record<string, ChartDocument> = {};
  for (const [oldId, newId] of chartIds) {
    const doc = (bundle.charts ?? {})[oldId];
    if (doc) charts[newId] = doc;
  }
  // §21/§4.4.2 An image pasted INTO a diagram is an ordinary `assets` row, referenced by the
  // scene's `fileIds` rather than by any slide content — so the survivor filter below, which
  // keeps only assets a remapped slide points at, would drop it and the copy would render
  // that diagram with holes. Registering those ids here is what carries them across, and it
  // is the same shape of bug §24.1.1 found in the deck logo.
  //
  // The ids are deliberately NOT remapped: an `assetId` is referenced from inside the scene's
  // own element data, so giving the copy fresh ones would mean rewriting the scene. The copy
  // keeps the same fileIds and gets its own `assets` ROWS (`writeDeckBundle` re-files every
  // blob under the new deckId) — two rows, same bytes, independent lifetimes, which is all
  // the cascade delete needs.
  for (const doc of Object.values(diagrams)) {
    for (const fileId of doc.fileIds ?? []) assetIds.set(fileId, fileId);
  }

  const assets: BundleAsset[] = [];
  for (const asset of bundle.assets) {
    const newId = assetIds.get(asset.assetId);
    if (newId) assets.push({ ...asset, assetId: newId });
  }

  // §17.5.3 The nugget bundles ride through untouched, ids included: they are the one thing
  // in here that must NOT be remapped, because the receiving database adopts them by id.
  return {
    deck,
    grids,
    diagrams,
    gantts,
    charts,
    assets,
    nuggets: bundle.nuggets ?? [],
  };
}

// §12.4.5 Duplicate: read, remap, write. Returns the new deck's id, or null when the
// source is gone (deleted in another page between listing and clicking).
export async function duplicateDeck(deckId: string): Promise<string | null> {
  const bundle = await readDeckBundle(deckId);
  if (!bundle) return null;
  const copy = remapBundle(bundle, { title: `${bundle.deck.title} (copy)` });
  return writeDeckBundle(copy);
}
